import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HeroProps {
  onBookClick: () => void;
  onTalkClick: () => void;
}

export default function Hero({ onBookClick, onTalkClick }: HeroProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-[140px] md:pt-0">
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/0f07baa5adf69d24e40d4d81d88ace24.jpg"
          alt="Luxury dental clinic"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay — layered for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
      </div>

      {/* Subtle gold grain texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'1\'/%3E%3C/svg%3E")',
          backgroundRepeat: 'repeat',
          backgroundSize: '128px',
        }}
      />

      {/* Content */}
      <div
        className={`relative z-10 text-center max-w-4xl mx-auto px-6 transition-all duration-1200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Eyebrow */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="w-12 h-px bg-amber-400/60" />
          <p
            className="text-xs font-medium tracking-[0.3em] uppercase text-amber-400/90"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t('hero.badge')}
          </p>
          <div className="w-12 h-px bg-amber-400/60" />
        </div>

        {/* Headline */}
        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal text-white leading-[1.1] mb-6"
          style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
        >
          {t('hero.title.line1')}
          <br />
          <em className="text-amber-300 not-italic font-medium">{t('hero.title.line2')}</em>
        </h1>

        {/* Subtitle */}
        <p
          className="text-base md:text-lg text-white/60 mb-12 max-w-xl mx-auto leading-relaxed font-light"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {t('hero.subtitle1')}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onBookClick}
            className="bg-amber-400 hover:bg-amber-300 text-black px-9 py-4 font-semibold text-sm tracking-wide transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.08em' }}
          >
            {t('hero.cta.primary')}
          </button>
          <button
            onClick={onTalkClick}
            className="border border-white/30 text-white/80 hover:text-white hover:border-white/60 px-9 py-4 font-medium text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.08em' }}
          >
            {t('hero.cta.secondary')}
          </button>
        </div>

        {/* Trust line */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-white/35 text-xs tracking-widest uppercase px-4" style={{ fontFamily: "'Inter', sans-serif" }}>
          <span className="whitespace-nowrap">Doctor-Supervised</span>
          <span className="text-amber-400/40 hidden sm:inline">·</span>
          <span className="whitespace-nowrap">JCI-Accredited</span>
          <span className="text-amber-400/40 hidden sm:inline">·</span>
          <span className="whitespace-nowrap">Save up to 70%</span>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
    </section>
  );
}
